import React, { useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { 
  makeStyles, 
  Typography, 
  Grid,
  Box,
  FormGroup,
  FormControlLabel,
  Switch,
  Card,
  Avatar,
  useTheme,
  Button,
} from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';

import { CanvasRead, ValueCanvasRead } from '../CreateIdeaDialogs';
import { StatusIdea, IdeaApproverTable } from '../../components';
import { AttachmentFiles } from "./AttachmentFiles"
import { AttachmentLink } from "./AttachmentLink";
import { AttachmentVideo } from "./AttachmentVideo"
import { AttachmentImages } from "./AttachmentImages";
import { Author, MenuInformation } from '../Shared';
import { ImagineLinkify } from "../TextLinkify";
import { ideaActions } from '../../actions';
import { history } from '../../helpers';
import iconBusinessModelCanvas from '../../assets/imgs/icon-businessmodelcanvas.svg';
import iconValuePropositionCanvas from '../../assets/imgs/icon-valuepropositioncanvas.svg';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(4),
    borderRadius: 8,
    border: '1px solid',
    borderColor: theme.palette.neutrals.high.medium
  },
  paddingTop: {
    paddingTop: theme.spacing(3)
  },
  paddingBottom: {
    paddingBottom: theme.spacing(3)
  },
  title: {
    fontSize: 14,
    textTransform: 'lowercase',
    fontWeight: 'bold',
  },
  titleUpperCase: {
    fontSize: 14,
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  content: {
    fontSize: 16,
    whiteSpace: 'pre-line'
  },
  contributorList: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    '&:first-child': {
      marginTop: 0,
    }
  },
  contributorImage: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    marginRight: theme.spacing(1)
  },
  contributorName: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    fontsize: 14,
    fontWeight: '600',
    color: theme.palette.neutrals.low.main,
    '&:hover':{
      color: theme.palette.primary.main,
    }
  },
  container: {
    paddingBottom: 24,
  },
  grid: {
    display: 'grid',
    gridAutoFlow: 'row',
    gridGap: 10,
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'auto',
    paddingTop: 16
  },
  buttonLinkCanvas: {
    width: 256,
    height: 80,
    borderRadius: 8,
    boxShadow: 'none',
    backgroundColor: theme.palette.neutrals.high.lightest,
    '&:hover': {
      boxShadow: 'none',
    },
    '&:last-child': {
      marginLeft: theme.spacing(3)
    }
  },
  textLinkCanvas: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.neutrals.low.dark,
  },
  statusAndCodeIdea: { 
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: "center",
     [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
        marginTop: 16
    },
  },
  challengeLink: {
    cursor: 'pointer',
    '&:hover':{
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    }
  }
}));

function IdeaGeneral() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { ideaDetails } = useSelector((state) => state.idea);
  const [isOpenCanvas, setIsOpenCanvas] = useState(false)
  const [isOpenValueCanvas, setIsOpenValueCanvas ] = useState(false)
  const user = useSelector(state => state.user);
  const { t } = useTranslation();

  function handleOnOpenValueCanvas(){
    setIsOpenValueCanvas(true)
  }

  function handleOnCloseValueCanvas(){
    setIsOpenValueCanvas(false)
  }

  function handleOnOpenCanvas(){
    setIsOpenCanvas(true)
  }

  function handleOnCloseCanvas(){
    setIsOpenCanvas(false)
  }

  function handleChange(status) {
    dispatch(ideaActions.sendTimelineView(ideaDetails._id, status));
  }

  function isFillCanvas(canvas) {
    if (!!canvas.length) {
      const hasAnswer = canvas.map((item) => item.answer?.length).some((length) => length > 0);
      return hasAnswer;
    };
    return false
  };

  function goToChallenge() {
    history.push(`/challenge/detail/${ideaDetails.challenge._id}`);
  }
  return (
    <Grid container spacing={2}>
      <CanvasRead idea={ideaDetails} isOpen={isOpenCanvas} onClose={handleOnCloseCanvas} />
      <ValueCanvasRead idea={ideaDetails} isOpen={isOpenValueCanvas} onClose={handleOnCloseValueCanvas} />
      
      <Grid item xs={12}>
        <Typography component="h1" className={clsx(classes.titleUpperCase, classes.paddingBottom)}>
          {t('ideia_geral')}
        </Typography>
        <Card elevation={0} className={classes.card}>
          <Grid item xs={12} style={{ marginLeft: -16, marginTop: -16 }}>
            <Author dateAndTime={ideaDetails.createdAt} userAuthor={ideaDetails.user} />
          </Grid>
          <Grid item xs={12}>
            <Box className={clsx(classes.statusAndCodeIdea, classes.paddingTop)}>
              <Box display="flex" alignItems="center">
                <Typography component="h1" className={classes.title}>
              {t('status')}:&nbsp;&nbsp;
                </Typography>
                <StatusIdea status={ideaDetails.statusIdea} />
              </Box>
              <Box display="flex" alignItems="center">
                <Typography component="h1" className={classes.title}>
                  código:&nbsp;&nbsp;
                </Typography>
                <Typography component="p" variant="subtitle1">
                  {ideaDetails.code}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {ideaDetails.challenge?.name && (
            <Grid item xs={12}>
              <Typography component="h1" className={clsx(classes.title, classes.paddingTop)}>
                {t('desafio').toUpperCase()}:&nbsp;
              </Typography>
              <Typography 
                component="p" 
                variant="subtitle1"
                className={classes.challengeLink}
                onClick={goToChallenge}
                >
                {ideaDetails.challenge.name}
              </Typography>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <Typography component="h1" className={clsx(classes.title, classes.paddingTop)}>
              {t('titulo').toUpperCase()}:&nbsp;
            </Typography>
            <Typography component="p" variant="subtitle1">
              {ideaDetails.name}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography component="h1" className={clsx(classes.title, classes.paddingTop)}>
              {t('descricao').toUpperCase()}:&nbsp;
            </Typography>
            <Typography component="p" variant="subtitle1">
              <ImagineLinkify>
                {ideaDetails.description}
              </ImagineLinkify>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {(isFillCanvas(ideaDetails.canvas) || isFillCanvas(ideaDetails.canvasValueProposition)) && (
              <Typography component="h1" className={clsx(classes.title, classes.paddingTop)}>
                CANVAS:&nbsp;
              </Typography>
            )}
            <Box marginTop="1rem">
              {isFillCanvas(ideaDetails.canvasValueProposition) && (
                <Button 
                  variant="contained"
                  onClick={handleOnOpenValueCanvas}
                  className={classes.buttonLinkCanvas}
                  startIcon={<img src={iconValuePropositionCanvas} alt="Value Proposition Canvas" />}
                >
                  <Typography 
                    component="p" 
                    variant="subtitle1"
                    className={classes.textLinkCanvas}
                  >
                    Value Proposition Canvas
                  </Typography>
                </Button>
              )}
              {isFillCanvas(ideaDetails.canvas) && (
                <Button 
                  variant="contained"
                  onClick={handleOnOpenCanvas}
                  className={classes.buttonLinkCanvas}
                  startIcon={<img src={iconBusinessModelCanvas} alt="Business Model Canvas" />}
                >
                  <Typography 
                    component="p" 
                    variant="subtitle1"
                    className={classes.textLinkCanvas}
                  >
                    Business Model Canvas
                  </Typography>
                </Button>
              )}
            </Box>
          </Grid>
           
          {(ideaDetails.links?.length > 0 || ideaDetails.attachments?.length > 0) && (
            <Grid item xs={12}>
              <Typography component="h1" className={clsx(classes.title, classes.paddingTop, classes.paddingBottom)}>
                {t('anexos').toUpperCase()}:
              </Typography>
              {ideaDetails.links?.length > 0 && (
                <AttachmentLink links={ideaDetails.links} />
            )}
              {ideaDetails.attachments?.length > 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <AttachmentFiles attachments={ideaDetails.attachments} />
                    <AttachmentVideo attachments={ideaDetails.attachments} />
                    <AttachmentImages attachments={ideaDetails.attachments} />
              </Grid>
                </Grid>
            )}
            </Grid>
          )}
          {ideaDetails.contributors?.length > 0 && (
            <Grid item xs={12}>
              <Typography component="h1" className={clsx(classes.title, classes.paddingTop)}>
                {t('participantes')}:
              </Typography>
              { ideaDetails.contributors?.length > 0 &&
                ideaDetails.contributors.map((contributor, index) => {
                  return (
                    <Box className={classes.contributorList}>
                      {contributor.attachment?.url ? (
                        <Avatar 
                          className={classes.contributorImage}
                          src={contributor.attachment?.url} 
                        />
                      ) : (
                        <Avatar 
                          className={classes.contributorImage}
                          style={{ backgroundColor: theme.palette.primary.light }}
                        >
                          <PersonIcon style={{ color: theme.palette.primary.main }} />
                        </Avatar>
                      )}
                      <Typography 
                        key={index}
                        className={classes.contributorName}
                        onClick={() => history.push(`/profile/${contributor._id}`)}
                      >
                        {contributor.name}
                      </Typography>
                    </Box>
                  );
                }
              )}
            </Grid>
          )}
            <Grid item xs={12}>
              <Typography component="h1" className={clsx(classes.title, classes.paddingTop)}>
                {t('abrangencia').toUpperCase()}:
              </Typography>
              <Typography component="p" variant="subtitle1">
                {ideaDetails?.businessUnitLocal?.length > 0 &&
                ideaDetails.businessUnitLocal.map((businessUnitLocal, index) => {
                  const lastIndex = ideaDetails.businessUnitLocal.length - 1;

                  return (
                    <span key={index}>
                      {businessUnitLocal?.businessUnit?.name} {' '} |  {' '}
                      {businessUnitLocal?.local?.name} {' '} |  {' '}
                      {businessUnitLocal?.occupationArea?.name}
                      {index < lastIndex && <span>, </span>}
                    </span>
                  );
                })}
            </Typography>
          </Grid>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Box display="flex" alignItems="center">
          <Typography component="h1" className={classes.titleUpperCase}>
            {t('qualificadores_atribuidos').toLocaleUpperCase()}
          </Typography>
          <MenuInformation>
            {t('possiveis_qualificadores_para_essa_ideia_sao_os_gestores_da_area_de_abrangencia_vinculada')}
          </MenuInformation>
        </Box>
        <IdeaApproverTable />
      </Grid>
      {(user.typeProfile.type === ('admin' || 'manager') && ideaDetails.statusIdea.code !== "AWAITING_QUALIFICATION") &&  (
        <Grid item xs={12}>
          <FormGroup>
            <FormControlLabel
            label={t('exibir_ideia_no_feed')}
              control={
                <Switch
                  color="primary"
                  checked={ideaDetails.timeline}
                  onChange={() => handleChange(!ideaDetails.timeline)}
                />
              }  
            />
          </FormGroup>
        </Grid> 
      )} 
    </Grid>
  );
}

export { IdeaGeneral };
